import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as todoApi from '../../../../services/api/todoApi';

/**
 * TodoContext — single fetch source for the admin ToDo aggregation.
 * The sidebar badge and the ToDo page both read from here so we don't
 * fetch twice. Polls every 60s; exposes a `refresh()` for mutations.
 *
 * 403 (non-admin) is silently absorbed as "no data" — the sidebar badge
 * stays hidden, the page shows an empty-state message.
 */
const DEFAULT = {
  loading: false,
  groups: [],
  errors: [],
  totalCount: 0,
  generatedAt: null,
  forbidden: false,
};

const TodoContext = createContext({ ...DEFAULT, refresh: () => {} });

const POLL_MS = 60000;

export const TodoProvider = ({ children }) => {
  // CompanyRoute mounts the app under path="/" so useParams() sees no
  // :companyId. Rest of the app sniffs it from window.location.pathname;
  // follow the same convention. Tracking `useLocation` keeps us in sync
  // when the route changes (e.g. switching tenants).
  const location = useLocation();
  // companyId is a UUID in tenant-scoped URLs. Reject non-UUID first segments
  // (login, select-company, forgot-password, …) so background polling stays
  // silent on public pages.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const firstSegment = (location?.pathname || window.location.pathname).split('/')[1] || '';
  const companyId = UUID_RE.test(firstSegment) ? firstSegment : null;
  const [state, setState] = useState(DEFAULT);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await todoApi.fetchTodos(companyId);
      if (!mountedRef.current) return;
      setState({
        loading: false,
        forbidden: false,
        groups: res.data?.groups || [],
        errors: res.data?.errors || [],
        totalCount: res.data?.totalCount || 0,
        generatedAt: res.data?.generatedAt || null,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err.response?.status;
      setState({
        ...DEFAULT,
        loading: false,
        forbidden: status === 403,
      });
    }
  }, [companyId]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const value = { ...state, refresh: load };
  return <TodoContext.Provider value={value}>{children}</TodoContext.Provider>;
};

export const useTodos = () => useContext(TodoContext);
